import React, { useEffect, useState } from 'react';
import './BattleVictory.css';

interface BattleVictoryProps {
  experience: number;
  gold?: number;
  loot: Array<{
    name: string;
    quantity: number;
    rarity: string;
  }>;
  tilesUnlocked?: string[];
  bonuses?: {
    dropRateBonus?: string;
    rareMaterialBonus?: string;
    goldBonus?: string;
    hasBonuses?: boolean;
  } | null;
  characterXP?: Array<{
    characterName: string;
    level: number;
    experience: number;
    experienceToNextLevel: number;
    xpGained: number;
    leveledUp: boolean;
    oldLevel: number;
    newLevel: number;
    levelsGained: number;
  }>;
  onContinue: () => void;
}

const BattleVictory: React.FC<BattleVictoryProps> = ({ experience, gold = 0, loot, tilesUnlocked = [], bonuses, characterXP = [], onContinue }) => {
  const [showContent, setShowContent] = useState(false);
  const [animatedExp, setAnimatedExp] = useState(0);
  const [showBonuses, setShowBonuses] = useState(false);
  const [showLoot, setShowLoot] = useState(false);
  const [showTiles, setShowTiles] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setShowContent(true), 100);

    // Animate experience counter
    const expDuration = 1500;
    const expSteps = 60;
    const expIncrement = experience / expSteps;
    let currentExp = 0;

    const expInterval = setInterval(() => {
      currentExp += expIncrement;
      if (currentExp >= experience) {
        setAnimatedExp(experience);
        clearInterval(expInterval);
        // Show bonuses after experience animation
        setTimeout(() => setShowBonuses(true), 300);
        // Show loot after bonuses
        setTimeout(() => setShowLoot(true), bonuses?.hasBonuses ? 800 : 500);
        // Show tiles after loot
        setTimeout(() => setShowTiles(true), bonuses?.hasBonuses ? 1300 : 1000);
      } else {
        setAnimatedExp(Math.floor(currentExp));
      }
    }, expDuration / expSteps);

    return () => clearInterval(expInterval);
  }, [experience, bonuses]);

  const getRarityColor = (rarity: string) => {
    switch (rarity.toLowerCase()) {
      case 'common': return '#9e9e9e';
      case 'uncommon': return '#4caf50';
      case 'rare': return '#2196f3';
      case 'epic': return '#9c27b0';
      case 'legendary': return '#ff9800';
      default: return '#fff';
    }
  };

  return (
    <div className="battle-victory-overlay">
      <div className={`victory-content ${showContent ? 'show' : ''}`}>
        <div className="victory-header">
          <h1 className="victory-title">VICTORY!</h1>
          <div className="victory-banner"></div>
        </div>

        <div className="victory-body">
          {characterXP && characterXP.length > 0 ? (
            <div className="experience-section">
              <h2>Experience Gained</h2>
              <div className="character-xp-list">
                {characterXP.map((char, index) => {
                  const xpProgress = ((char.experience / char.experienceToNextLevel) * 100).toFixed(1);
                  return (
                    <div key={index} className="character-xp-item">
                      <div className="character-xp-name">{char.characterName}</div>
                      {char.leveledUp ? (
                        <div className="character-xp-level">
                          <span style={{color: '#FFD700', fontWeight: 'bold', fontSize: '1.1em'}}>
                            ⭐ LEVEL UP! {char.oldLevel} → {char.newLevel}
                          </span>
                          {char.levelsGained > 1 && (
                            <span style={{color: '#FFD700', marginLeft: '0.5rem'}}>
                              (+{char.levelsGained} levels!)
                            </span>
                          )}
                          <span style={{color: '#4caf50', marginLeft: '0.5rem'}}>+{char.xpGained} XP</span>
                        </div>
                      ) : (
                        <div className="character-xp-level">Level {char.level} <span style={{color: '#4caf50', marginLeft: '0.5rem'}}>+{char.xpGained} XP</span></div>
                      )}
                      <div className="character-xp-progress">
                        <div className="xp-bar-container">
                          <div className="xp-bar-fill" style={{width: `${xpProgress}%`}}></div>
                        </div>
                        <div className="xp-text">{char.experience} / {char.experienceToNextLevel} XP ({xpProgress}%)</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="experience-section">
              <h2>Experience Gained</h2>
              <div className="exp-amount">+{animatedExp} XP</div>
            </div>
          )}

          {showBonuses && bonuses?.hasBonuses && (
            <div className="bonuses-section">
              <h2>🏰 Town Bonuses Active!</h2>
              <div className="bonuses-grid">
                {bonuses.dropRateBonus && (
                  <div className="bonus-item">
                    <span className="bonus-icon">💎</span>
                    <span className="bonus-label">Material Drop Rate:</span>
                    <span className="bonus-value">{bonuses.dropRateBonus}</span>
                  </div>
                )}
                {bonuses.rareMaterialBonus && (
                  <div className="bonus-item">
                    <span className="bonus-icon">✨</span>
                    <span className="bonus-label">Rare Material Chance:</span>
                    <span className="bonus-value">{bonuses.rareMaterialBonus}</span>
                  </div>
                )}
                {bonuses.goldBonus && (
                  <div className="bonus-item">
                    <span className="bonus-icon">💰</span>
                    <span className="bonus-label">Gold from Battles:</span>
                    <span className="bonus-value">{bonuses.goldBonus}</span>
                  </div>
                )}
              </div>
              <p className="bonuses-note">Your liberated towns are providing these bonuses!</p>
            </div>
          )}

          {showLoot && gold > 0 && (
            <div className="gold-section">
              <h2>💰 Gold Earned</h2>
              <div className="gold-amount">+{gold} Gold</div>
            </div>
          )}

          {showLoot && loot.length > 0 && (
            <div className="loot-section">
              <h2>Loot Acquired</h2>
              <div className="loot-grid">
                {loot.map((item, index) => (
                  <div
                    key={index}
                    className="loot-item"
                    style={{
                      animationDelay: `${index * 0.1}s`,
                      borderColor: getRarityColor(item.rarity)
                    }}
                  >
                    <div className="loot-quantity">x{item.quantity}</div>
                    <div className="loot-name" style={{ color: getRarityColor(item.rarity) }}>
                      {item.name}
                    </div>
                    <div className="loot-rarity">{item.rarity}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showLoot && loot.length === 0 && (
            <div className="no-loot-section">
              <p>No items dropped this time...</p>
            </div>
          )}

          {showTiles && tilesUnlocked && tilesUnlocked.length > 0 && (
            <div className="tiles-section">
              <h2>New Areas Unlocked!</h2>
              <div className="tiles-grid">
                {tilesUnlocked.map((tileName, index) => (
                  <div
                    key={index}
                    className="tile-unlocked"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="tile-icon">🗺️</div>
                    <div className="tile-name">{tileName}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="victory-footer">
          <button
            className="continue-btn"
            onClick={onContinue}
            disabled={!showLoot && (loot.length > 0 || (tilesUnlocked && tilesUnlocked.length > 0))}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default BattleVictory;
